import React, { useState, useEffect } from 'react';
import { FaUserCircle } from 'react-icons/fa';
import { Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { useNavigate } from 'react-router-dom';
import { endSpectraSession } from '../utils/session';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export default function Profile() {
  const navigate = useNavigate();

  const [equityChartData, setEquityChartData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
    datasets: [
      {
        label: 'Equity',
        data: [1000, 1200, 900, 1400, 1600],
        borderColor: '#ffd700',
        backgroundColor: 'rgba(255, 215, 0, 0.2)',
        tension: 0.4,
        fill: true,
        pointRadius: 5,
      },
    ],
  });

  const [plChartData, setPlChartData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
    datasets: [
      {
        label: 'P/L %',
        data: [5, 10, -3, 12, 8],
        borderColor: '#00bfff',
        backgroundColor: 'rgba(0, 191, 255, 0.2)',
        tension: 0.3,
        fill: true,
        pointRadius: 5,
      },
    ],
  });

  const [allocationData, setAllocationData] = useState({
    labels: ['Physical allocation', 'Coin Value', 'Spectra Cash'],
    datasets: [
      {
        label: 'Portfolio Allocation',
        data: [50, 30, 20],
        backgroundColor: ['#ffd700', '#00bfff', '#ff6347'],
        borderWidth: 1,
      },
    ],
  });

  // User info
  const user = {
    username: 'JohnDoe',
    email: 'johndoe@example.com',
    lastLogin: '2025-09-10 14:32',
    accountType: 'Standard',
    totalInvestments: '$12,450',
    transactionsCount: 23,
  };

  // Extra stats for analytics
  const extraStats = [
    { title: 'Daily High', value: '$15.20' },
    { title: 'Daily Low', value: '$14.30' },
    { title: 'Other Coin Stats', value: '$8,540' },
  ];

  // Load dashboard data if needed (like in dashboard)
  useEffect(() => {
    const savedData = JSON.parse(localStorage.getItem('dashboardData'));
    if (savedData) {
      setEquityChartData(savedData.equityChartData || equityChartData);
      setPlChartData(savedData.plChartData || plChartData);
      setAllocationData(savedData.allocationData || allocationData);
    }
  }, []);

  return (
    <div style={{ padding: '40px 24px', maxWidth: '1000px', margin: '0 auto', fontFamily: 'Arial, sans-serif', background: '#f5f5f5', minHeight: '100vh' }}>
      <h1 style={{ textAlign: 'center', marginBottom: '40px', color: '#333' }}>Profile</h1>

      {/* User Info Section */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        marginBottom: '40px',
        background: '#fff',
        padding: '24px',
        borderRadius: '16px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
      }}>
        <FaUserCircle size={100} color="#ffd700" />
        <div style={{ fontSize: '16px', color: '#555' }}>
          <p><strong>Username:</strong> {user.username}</p>
          <p><strong>Email:</strong> {user.email}</p>
          <p><strong>Account Type:</strong> {user.accountType}</p>
          <p><strong>Last Login:</strong> {user.lastLogin}</p>
        </div>
      </div>

      {/* Account Stats */}
      <div style={{ display: 'flex', gap: '24px', marginBottom: '40px', flexWrap: 'wrap' }}>
        <div style={statCardStyle}>
          <h3>Total Investments</h3>
          <p style={statNumberStyle}>{user.totalInvestments}</p>
        </div>
        <div style={statCardStyle}>
          <h3>Transactions</h3>
          <p style={statNumberStyle}>{user.transactionsCount}</p>
        </div>
      </div>

      {/* Analytics Section */}
      <div style={{ background: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2>Portfolio Analytics</h2>
          <img src="/spectra-logo.jpg" alt="Spectra Logo" style={{ height: '50px' }} />
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {/* Equity Chart */}
          <div style={{ flex: '1 1 400px', minHeight: '300px' }}>
            <Line id="equityChart" data={equityChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>

          {/* P/L Chart */}
          <div style={{ flex: '1 1 400px', minHeight: '300px' }}>
            <Line id="plChart" data={plChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>

          {/* Pie Chart */}
          <div style={{ flex: '1 1 400px', minHeight: '300px' }}>
            <Pie id="allocationChart" data={allocationData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>

        {/* Extra Stats Boxes */}
        <div style={{ display: 'flex', gap: '16px', marginTop: '24px', flexWrap: 'wrap' }}>
          {extraStats.map((s, i) => (
            <div key={i} style={statCardStyle}>
              <h4>{s.title}</h4>
              <p style={statNumberStyle}>{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Account Actions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '400px', margin: '0 auto' }}>
        <button style={buttonStyle} onClick={() => navigate('/profile/edit')}>Edit Profile</button>
        <button style={buttonStyle} onClick={() => navigate('/profile/password')}>Change Password</button>
        <button style={{ ...buttonStyle, background: '#c60707ff', color: '#fff' }} onClick={endSpectraSession}>Logout</button>
      </div>
    </div>
  );
}

const statCardStyle = {
  flex: '1 1 200px',
  background: '#fff',
  padding: '24px',
  borderRadius: '16px',
  textAlign: 'center',
  boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
  minWidth: '150px'
};

const statNumberStyle = {
  fontSize: '28px',
  fontWeight: 'bold',
  color: '#333',
  marginTop: '12px'
};

const buttonStyle = {
  padding: '14px',
  borderRadius: '8px',
  border: 'none',
  background: '#ffd700',
  color: '#111',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '16px'
};
